import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import _ from 'lodash';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import cn from 'classnames';
import PictionJS from 'piction-js';
import appConfig from '../../appConfig';
import { retrieveUMOs } from '../../actions/umoGallery';
import { DynamicTag, SearchParams } from '../../utils/searchUtils';
import { DATE_FORMAT } from '../../constants/session';
import UMOInsertThumbnails from './UMOInsertThumbnails';

function renderItem(
  customClass,
  displayName,
  fullName,
  value,
  isRepeatingField,
  key,
  showValueOnly,
  shouldParse,
  isKeyword,
  searchKeyword,
  separator,
  type) {
  return (
    <div
      key={key}
      className={cn({
        'meta-table__item': true,
        'meta-table__item--repeating': isRepeatingField,
        'meta-table__item--value-only': showValueOnly,
      }, customClass)}
    >
      {
        !showValueOnly &&
        <div className={cn(customClass ? `${customClass}-name` : 'meta-table__item-name')}>
          {displayName}
        </div>
      }
      {
        type === 'UMO_INSERT' && _.isArray(value) && value.length > 0 ?
          <UMOInsertThumbnails
            umoIDs={value}
            onThumbnailClick={(id) => searchKeyword(isKeyword, id, fullName)}
          />
          :
        isKeyword ?
          <div className="meta-table__value-list">
            {_.map(value, (keyword, i) =>
              _.isEmpty(keyword) ? '' :
                <Button
                  className="meta-table__item-value"
                  variant="link"
                  onClick={() => { searchKeyword(isKeyword, keyword, fullName); }}
                  key={keyword}
                >
                  {keyword}
                  {i < value.length - 1 ? separator : ''}
                </Button>
            )}
          </div>
          :
          <div className="meta-table__item-value">
            {shouldParse && !_.isEmpty(value) ? parse(value) : (
              type === 'LINK' ?
              <a href={value} target='_blank'>{value}</a>
              :
              value
            )}
          </div>
      }
    </div>
  );
}

function MetadataViewItem({
  metadata, showValueOnly, parseList, keywordDelim, dispatch, toggleUMODetailView, customClass,
}) {
  let isKeyword = (metadata.type === 'KEYWORD' && 'KEYWORD') ||
    (metadata.type === 'UMO_INSERT' && 'UMO_ID');
  let value = metadata.value;
  let fullName = `${metadata.data.$.i}.${metadata.data.$.c}`;
  if (metadata.type === 'DATE' && value) {
    value = format(value, appConfig.dateFormat || DATE_FORMAT);
  }
  const shouldParse = (metadata.data.$.api && metadata.data.$.api === 'RICH') || (parseList && parseList.includes(metadata.id));
  // if a custom delimeter value is found- use that, otherwise use a coma
  let separator = keywordDelim && keywordDelim.length > 0 ?
    keywordDelim.find(i => i.id === metadata.id) : null;
  separator = separator ? separator.delimeter : ',';

  if (isKeyword && metadata.value) {
    value = shouldParse ? [metadata.value] : _.split(metadata.value, separator).map((v) => v.trim()).filter(Boolean);
  }
  const searchKeyword = (searchKey, keyword, searchName) => {
    let searchVal = '';
    if (searchKey === 'KEYWORD') {
      searchVal = `(META:"${searchName},${keyword}")`;
    } else if (searchKey === 'UMO_ID') {
      searchVal = `(UMO_ID:"${keyword}")`;
    }
    const searchTag = new DynamicTag(searchVal, keyword);
    const searchParams = new SearchParams({ tagList: [searchTag] });
    dispatch(retrieveUMOs(searchParams));
    if (toggleUMODetailView) toggleUMODetailView(false);
  };

  if (metadata.type === 'RGROUP' && !showValueOnly) {
    const repeatingGroups = [];

    if (metadata.data.rgroup[0].mv) {
      metadata.data.rgroup[0].mv.forEach((md) => {
        if (md) {
          value = md._;
          fullName = `${md.$.i}.${md.$.c}`;
          if (md.$.t === 'DATE' && value) {
            value = format(value, appConfig.dateFormat || DATE_FORMAT);
          }
          isKeyword = (md.$.t === 'KEYWORD' && 'KEYWORD') || (md.$.t === 'UMO_INSERT' && 'UMO_ID');
          // if a custom delimeter value is found- use that, otherwise use a coma
          separator = keywordDelim && keywordDelim.length > 0 ?
            keywordDelim.find(i => i.id === md.$.id) : null;
          separator = separator ? separator.delimeter : ',';
          const isParse = (md.$.api && md.$.api === 'RICH') || (parseList && parseList.includes(md.$.id));
          if (isKeyword && md._) value = isParse ? [md._] : _.split(md._, separator).map((v) => v.trim()).filter(Boolean);
          const repeatingGroupIndex = md.$.p;

          if (md._ || !appConfig.removeEmptyMetatags) {
            if (!repeatingGroups[repeatingGroupIndex]) {
              repeatingGroups[repeatingGroupIndex] = [];
            }

            repeatingGroups[repeatingGroupIndex].push(renderItem(
              customClass, md.$.d, fullName, value, true, `${md.$.d}_${md.$.p}`,
              false, isParse, isKeyword, searchKeyword, separator));
          }
        }
      });
    }

    return (repeatingGroups.length > 0 ?
        <div className="meta-table__item-rfield-header">
          {renderItem(customClass, metadata.displayName, fullName, metadata.value)}
          {_.flatten(repeatingGroups.map((repeatingGroup, i) =>
            [<div key={i} className="meta-table__item-rfield-head" />, ...repeatingGroup]))}
        </div> : null
    );
  }

  return appConfig.removeEmptyMetatags && !value ? null : renderItem(
    customClass, metadata.displayName, fullName, value, false, null,
    showValueOnly, shouldParse, isKeyword, searchKeyword, separator,
    metadata.type
  );
}

MetadataViewItem.propTypes = {
  metadata: PropTypes.instanceOf(PictionJS.Metadata),
  showValueOnly: PropTypes.bool,
  parseList: PropTypes.instanceOf(Array),
  dispatch: PropTypes.func,
  toggleUMODetailView: PropTypes.func,
  keywordDelim: PropTypes.instanceOf(Array),
  customClass: PropTypes.string,
};

export default MetadataViewItem;
